import React, { useState, useEffect } from 'react';
import { auth } from './auth';

// Blocking 2FA enrolment screen. Used in two places:
//  - At sign-in, when a company requires 2FA and the user has not enrolled. Pass the
//    short-lived enrol token (there is no session yet).
//  - Inside the app, when a session arrived another way (invitation, password reset,
//    signup) for a user who still must enrol. Omit enrolToken to use the session.
// On success it calls onDone(user) with the now-enrolled user.
export default function MfaEnrollmentGate({ enrolToken, primaryColour = '#111827', onDone }) {
  const [step, setStep] = useState('show');   // 'show' | 'codes'
  const [data, setData] = useState(null);      // { secret, provisioning_uri }
  const [qrUrl, setQrUrl] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true); setError(null);
      try {
        const d = await auth.mfaEnrolStart(enrolToken);
        if (cancelled) return;
        setData(d);
        try { const u = await auth.mfaEnrolQr(enrolToken); if (!cancelled) setQrUrl(u); }
        catch (e) { /* QR is optional, the manual key still works */ }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Could not start 2FA setup. Please sign in again.');
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [enrolToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const { user: u, backup_codes } = await auth.mfaEnrolConfirm(enrolToken, code, password);
      setBackupCodes(backup_codes);
      setUser(u);
      setStep('codes');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not enable 2FA. Check the code and your password.');
    } finally { setBusy(false); }
  };

  const copySecret = () => { try { navigator.clipboard.writeText(data?.secret || ''); } catch (e) { /* clipboard blocked */ } };
  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, marginBottom: 12, fontFamily: 'inherit', outline: 'none' };

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '36px 32px', width: '100%', maxWidth: 460 }}>
        {step === 'show' && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, marginTop: 0, marginBottom: 6 }}>Set up two-factor authentication</h1>
            <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 18 }}>Your organisation requires 2FA. Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy, and similar), then enter the 6-digit code and your password to finish.</p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              {qrUrl
                ? <img src={qrUrl} alt="2FA QR code" style={{ width: 180, height: 180, border: '1px solid #E5E7EB', borderRadius: 10 }} />
                : <div style={{ width: 180, height: 180, border: '1px dashed #D1D5DB', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12, textAlign: 'center', padding: 10 }}>{busy ? 'Loading…' : 'QR unavailable. Use the key below.'}</div>}
            </div>
            {data?.secret && (
              <div style={{ background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', marginBottom: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 4 }}>Or enter this key manually</div>
                <div style={{ fontFamily: 'monospace', fontSize: 13, letterSpacing: 1, wordBreak: 'break-all', color: '#111827' }}>{data.secret}</div>
                <button type="button" onClick={copySecret} style={{ background: 'transparent', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 11, marginTop: 6, padding: 0 }}>Copy key</button>
              </div>
            )}
            <form onSubmit={submit}>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="6-digit code" value={code} onChange={e => setCode(e.target.value)} required maxLength={6} style={{ ...inp, fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' }} />
              <input type="password" autoComplete="current-password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} required style={inp} />
              {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
              <button type="submit" disabled={busy || !code || !password} style={{ width: '100%', background: busy ? '#9CA3AF' : primaryColour, color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontWeight: 600, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer' }}>{busy ? 'Enabling…' : 'Verify and enable'}</button>
            </form>
          </>
        )}
        {step === 'codes' && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, marginTop: 0, marginBottom: 6 }}>Save your backup codes</h1>
            <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>2FA is now on. Store these one-time backup codes somewhere safe. Each one works once if you lose access to your authenticator. They will not be shown again.</p>
            <div style={{ background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontFamily: 'monospace', fontSize: 13 }}>
              {(backupCodes || []).map((c, i) => <span key={i} style={{ color: '#111827' }}>{c}</span>)}
            </div>
            <button type="button" onClick={() => { if (user) onDone(user); }} style={{ width: '100%', background: primaryColour, color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>I have saved them, continue</button>
          </>
        )}
      </div>
    </div>
  );
}
